import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useUserAuth } from "../context/UserAuthContext";
import { db } from "../config/fbConfig";
import { collection, getDocs, query, where, addDoc, serverTimestamp, doc, getDoc, updateDoc, arrayUnion } from "firebase/firestore";
import Loader from "./Loader";
import Modal from 'react-modal';
import SuccessModal from "./SuccessModal";
import Html5QrcodePlugin from './Html5QrcodePlugin';
import '../styles/Sales.css';
import generateReceiptPDF from './ReceiptGenerator';

Modal.setAppElement('#root');

const SalesEntry = () => {
  const { user } = useUserAuth();
  const [cart, setCart] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedProduct, setSelectedProduct] = useState("");
  const [printContent, setPrintContent] = useState("");
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isScanModalOpen, setIsScanModalOpen] = useState(false);
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [cashierFirstName, setCashierFirstName] = useState("");
  const [cashierLastName, setCashierLastName] = useState("");
  const [cashierPhone, setCashierPhone] = useState("");
  const [cashierStatus, setCashierStatus] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [printerConnected, setPrinterConnected] = useState(false);
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [bookingSearch, setBookingSearch] = useState("");
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [linkLoading, setLinkLoading] = useState(false);
  const [roomMap, setRoomMap] = useState({});
  const [userDepartment, setUserDepartment] = useState(null);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const activeProductsQuery = query(collection(db, "products"), where("isActive", "==", true));
        const activeProductsSnapshot = await getDocs(activeProductsQuery);
        const activeProducts = activeProductsSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setProducts(activeProducts);
      } catch (error) {
        console.error("Error fetching products:", error);
        setError("Failed to fetch products: " + error.message);
        setShowModal(true);
      }
    };

    const fetchUserData = async () => {
      if (!user?.uid) {
        setLoading(false);
        return;
      }

      try {
        const userRef = doc(db, "users", user.uid);
        const userDoc = await getDoc(userRef);

        if (userDoc.exists()) {
          const userData = userDoc.data() || {};   // safeguard
          setCashierFirstName(userData.firstName || "");
          setCashierLastName(userData.lastName || "");
          setCashierPhone(userData.phone || "");
          setCashierStatus(userData.status || "");
          setUserDepartment(userData.department || null);
        } else {
          console.warn("User document does not exist in Firestore");
          // Optionally set defaults or show message
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
        setError("Failed to fetch user data: " + (error.message || "Unknown error"));
        setShowModal(true);
      } finally {
        setLoading(false);
      }
    };

    const checkPrinterConnection = async () => {
      try {
        const devices = await navigator.usb.getDevices();
        setPrinterConnected(devices.length > 0);
      } catch (err) {
        console.error("Error checking printer connection:", err);
        setPrinterConnected(false);
      }
    };

    fetchProducts();
    fetchUserData();
    checkPrinterConnection();
  }, [user.uid]);

  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredProducts([]);
      return;
    }

    const lowerQuery = searchQuery.toLowerCase();

    let candidates = products;

    // If cashier has department → only show matching products
    if (userDepartment) {
      candidates = products.filter(p => (p.department || "").trim() === userDepartment.trim());
    }

    const filtered = candidates.filter(
      (product) =>
        product.name?.toLowerCase().includes(lowerQuery) ||
        product.category?.toLowerCase().includes(lowerQuery) ||
        (product.barcode && typeof product.barcode === 'string' && 
        product.barcode.toLowerCase().includes(lowerQuery))
    );

    setFilteredProducts(filtered);
  }, [searchQuery, products, userDepartment]);

  useEffect(() => {
    if (selectedProduct) {
      const product = products.find((p) => p.id === selectedProduct);
      if (product) {
        if (product.quantity < 1) {
          setError(`Product ${product.name} is out of stock`);
          setShowModal(true);
          return;
        }
        setCart((prevCart) => {
          const existingItem = prevCart.find((item) => item.id === product.id);
          if (existingItem) {
            if (existingItem.quantity + 1 > product.quantity) {
              setError(`Cannot add more ${product.name}. Only ${product.quantity} in stock`);
              setShowModal(true);
              return prevCart;
            }
            return prevCart.map((item) =>
              item.id === product.id
                ? {
                    ...item,
                    quantity: item.quantity + 1,
                    subtotal: item.price * (item.quantity + 1),
                    totalCost: (item.costPrice || 0) * (item.quantity + 1),
                    profit: (item.price - (item.costPrice || 0)) * (item.quantity + 1)
                  }
                : item
            );
          } else {
            const costPrice = product.costPrice || 0;
            const newItem = { 
              ...product, 
              quantity: 1, 
              subtotal: product.price * 1,
              totalCost: costPrice * 1,
              profit: (product.price - costPrice) * 1
            };
            return [...prevCart, newItem];
          }
        });
        setSelectedProduct("");
        setSearchQuery("");
      }
    }
  }, [selectedProduct, products]);

  const onNewScanResult = (decodedText) => {
    setSearchQuery(decodedText);
    const product = products.find((p) => p.barcode && typeof p.barcode === 'string' && p.barcode.toLowerCase() === decodedText.toLowerCase());
    if (product) {
      setSelectedProduct(product.id);
    }
    setIsScanModalOpen(false);
  };

  const updateQuantity = (id, newQuantity) => {
    const quantity = newQuantity === "" ? "" : Math.max(1, parseInt(newQuantity) || 1);
    const product = products.find((p) => p.id === id);
    if (quantity > product.quantity) {
      setError(`Cannot set quantity to ${quantity}. Only ${product.quantity} ${product.name} in stock`);
      setShowModal(true);
      return;
    }
    setCart((prevCart) =>
      prevCart.map((item) => {
        if (item.id === id) {
          const costPrice = item.costPrice || 0;
          const qty = quantity === "" ? 1 : quantity;
          return { 
            ...item, 
            quantity: qty, 
            subtotal: item.price * qty,
            totalCost: costPrice * qty,
            profit: (item.price - costPrice) * qty
          };
        }
        return item;
      })
    );
  };

  const incrementQuantity = (id) => {
    setCart((prevCart) =>
      prevCart.map((item) => {
        const product = products.find((p) => p.id === item.id);
        if (item.id === id && item.quantity + 1 <= product.quantity) {
          const costPrice = item.costPrice || 0;
          const newQuantity = item.quantity + 1;
          return { 
            ...item, 
            quantity: newQuantity, 
            subtotal: item.price * newQuantity,
            totalCost: costPrice * newQuantity,
            profit: (item.price - costPrice) * newQuantity
          };
        } else if (item.id === id) {
          setError(`Cannot add more ${item.name}. Only ${product.quantity} in stock`);
          setShowModal(true);
          return item;
        }
        return item;
      })
    );
  };

  const decrementQuantity = (id) => {
    setCart((prevCart) =>
      prevCart.map((item) => {
        if (item.id === id && item.quantity > 1) {
          const costPrice = item.costPrice || 0;
          const newQuantity = item.quantity - 1;
          return { 
            ...item, 
            quantity: newQuantity, 
            subtotal: item.price * newQuantity,
            totalCost: costPrice * newQuantity,
            profit: (item.price - costPrice) * newQuantity
          };
        }
        return item;
      })
    );
  };

  const removeFromCart = (id) => {
    setCart(cart.filter((item) => item.id !== id));
  };

  const calculateTotal = () => {
    return cart.reduce((total, item) => total + item.subtotal, 0);
  };

  const calculateTotalCost = () => {
    return cart.reduce((total, item) => total + (item.totalCost || 0), 0);
  };

  const calculateTotalProfit = () => {
    return cart.reduce((total, item) => total + (item.profit || 0), 0);
  };

  const wrapText = (text, maxWidth) => {
    const words = text.split(' ');
    let lines = [];
    let currentLine = '';

    words.forEach(word => {
      if ((currentLine + word).length <= maxWidth) {
        currentLine += (currentLine ? ' ' : '') + word;
      } else {
        if (currentLine) lines.push(currentLine);
        currentLine = word;
      }
    });
    if (currentLine) lines.push(currentLine);
    return lines.map(line => {
      const padding = Math.floor((maxWidth - line.length) / 2);
      return ' '.repeat(padding > 0 ? padding : 0) + line;
    }).join('\n');
  };

  const saveSale = async () => {
    if (cart.length === 0) return;
    try {
      // Validate stock before saving
      for (const item of cart) {
        const product = products.find((p) => p.id === item.id);
        if (item.quantity > product.quantity) {
          throw new Error(`Insufficient stock for ${item.name}. Available: ${product.quantity}`);
        }
      }

      // Prepare products array with all required fields
      const productsArray = cart.map((item) => ({
        name: item.name,
        quantity: item.quantity,
        total: item.subtotal,
        unitPrice: item.price,
        costPrice: item.costPrice || 0,
        totalCostPrice: (item.costPrice || 0) * item.quantity,
        profit: item.subtotal - ((item.costPrice || 0) * item.quantity),
        department: item.department || null,
      }));

      const cashierName = `${cashierFirstName} ${cashierLastName}`.trim() || user.email || 'Unknown';
      const totalProfit = calculateTotalProfit();
      const totalCost = calculateTotalCost();
      const grandTotal = calculateTotal();
      
      // Save sale to Firestore with all required fields
      await addDoc(collection(db, "sales"), {
        userId: user.uid,
        cashierName,
        cashierPhone: cashierPhone || '',
        cashierStatus: cashierStatus || '',
        cashierDepartment: userDepartment || null,
        timestamp: serverTimestamp(),
        customerName: customerName || '',
        customerPhone: customerPhone || '',
        products: productsArray,
        grandTotal: grandTotal,
        totalCost: totalCost,
        totalProfit: totalProfit,
        profitMargin: grandTotal > 0 ? (totalProfit / grandTotal) * 100 : 0
      });

      // Update product quantities
      for (const item of cart) {
        const productRef = doc(db, "products", item.id);
        const product = products.find((p) => p.id === item.id);
        await updateDoc(productRef, {
          quantity: product.quantity - item.quantity
        });
      }

      // Update local products state
      setProducts((prevProducts) =>
        prevProducts.map((product) => {
          const cartItem = cart.find((item) => item.id === product.id);
          if (cartItem) {
            return { ...product, quantity: product.quantity - cartItem.quantity };
          }
          return product;
        })
      );

      setCart([]);
      setCustomerName("");
      setCustomerPhone("");
      setSuccessMessage("Sale saved successfully");
      setShowModal(true);
    } catch (error) {
      console.error("Error saving sale:", error);
      setError("Failed to save sale: " + error.message);
      setShowModal(true);
    }
  };

  const handlePrint = async () => {
    try {
      const pdfDoc = generateReceiptPDF(
        cart, 
        user, 
        cashierFirstName, 
        cashierLastName, 
        customerName, 
        customerPhone,
        new Date()
      );
      pdfDoc.save(`Receipt_${new Date().toISOString().replace(/[:.]/g, '-')}.pdf`);
      saveSale();
    } catch (error) {
      setError(error.message);
      setShowModal(true);
    }
  };

  const fetchActiveBookings = async () => {
    try {
      const q = query(
        collection(db, "bookings"),
        where("status", "in", ["pending", "checked-in", "confirmed"])
      );

      const snapshot = await getDocs(q);
      const bookingList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setBookings(bookingList);
    } catch (err) {
      console.error("Failed to load bookings:", err);
      setError("Could not load bookings");
      setShowModal(true);
    }
  };

  const handleLinkToBooking = async () => {
    if (!selectedBooking || cart.length === 0) return;

    setLinkLoading(true);

    try {
      // ── 1. Validate stock (same as saveSale)
      for (const item of cart) {
        const product = products.find((p) => p.id === item.id);
        if (item.quantity > product.quantity) {
          throw new Error(`Insufficient stock for ${item.name}. Available: ${product.quantity}`);
        }
      }

      // ── 2. Prepare products array (same format as normal sale)
      const productsArray = cart.map((item) => ({
        name: item.name,
        quantity: item.quantity,
        total: item.subtotal,
        unitPrice: item.price,
        costPrice: item.costPrice || 0,
        totalCostPrice: (item.costPrice || 0) * item.quantity,
        profit: item.subtotal - ((item.costPrice || 0) * item.quantity),
        department: item.department || null,
      }));

      const grandTotal = calculateTotal();
      const totalCost = calculateTotalCost();
      const totalProfit = calculateTotalProfit();

      const cashierName = `${cashierFirstName} ${cashierLastName}`.trim() || user.email || "Unknown";

      // Use GUEST data from the booking
      const guestName = selectedBooking.guestName || "Unknown Guest";
      const guestPhone = selectedBooking.guestPhone || null;

      // ── 3. Save to main "sales" collection
      const saleRef = await addDoc(collection(db, "sales"), {
        userId: user.uid,
        cashierName,
        cashierPhone: cashierPhone || '',
        cashierStatus: cashierStatus || '',
        timestamp: serverTimestamp(),
        customerName: guestName,                    // ← from booking
        customerPhone: guestPhone,                  // ← from booking
        products: productsArray,
        grandTotal,
        totalCost,
        totalProfit,
        profitMargin: grandTotal > 0 ? (totalProfit / grandTotal) * 100 : 0,

        // ── Extra fields to mark this sale as linked
        isLinked: true,
        linkedBookingId: selectedBooking.id,
        source: "linked-booking",                   // optional but useful for reports
      });

      // ── 4. Also attach summary to the booking document
      const saleDetailForBooking = {
        saleId: saleRef.id,               // still useful
        // timestamp: serverTimestamp(),
        cashierName,
        grandTotal,
        products: productsArray,          // ← the full array with name, qty, unitPrice, total, etc.
        // optional: source: "linked-booking",
      };

      await updateDoc(doc(db, "bookings", selectedBooking.id), {
        linkedSales: arrayUnion(saleDetailForBooking),
        lastLinkedSaleAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      // ── 5. Update stock (same as saveSale)
      for (const item of cart) {
        const productRef = doc(db, "products", item.id);
        const product = products.find((p) => p.id === item.id);
        await updateDoc(productRef, {
          quantity: product.quantity - item.quantity
        });
      }

      // Update local products state
      setProducts((prevProducts) =>
        prevProducts.map((product) => {
          const cartItem = cart.find((item) => item.id === product.id);
          if (cartItem) {
            return { ...product, quantity: product.quantity - cartItem.quantity };
          }
          return product;
        })
      );

      setSuccessMessage(`Sale linked and saved for ${guestName}`);
      setShowModal(true);

      // Reset
      setCart([]);
      setCustomerName("");
      setCustomerPhone("");
      setIsLinkModalOpen(false);
      setSelectedBooking(null);

    } catch (err) {
      console.error("Link & save failed:", err);
      setError("Failed to link and save sale: " + (err.message || "Unknown error"));
      setShowModal(true);
    } finally {
      setLinkLoading(false);
    }
  };

  const fetchRoomsForDisplay = async () => {
    try {
      const roomsSnap = await getDocs(collection(db, "rooms"));
      const map = {};
      roomsSnap.forEach(doc => {
        const data = doc.data();
        if (data.roomNumber) {
          map[doc.id] = data.roomNumber;
        }
      });
      setRoomMap(map);
    } catch (err) {
      console.warn("Could not load room numbers", err);
    }
  };

  if (loading) {
    return <Loader text={"LOADING"} />;
  }

  return (
    <>
      <div className="top-elements row align-center space-between">
        <Link to="/home" className="back-link">
          <i className="fa-solid fa-arrow-left size3"></i>
        </Link>
        <h2 className="manage-page-heading caps size4 margin-top-cashflow1">SALES ENTRY</h2>
        <Link to="#">
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
        </Link>
      </div>
      <div className="login-page-items login-page-items-cashflow scroll-fixed scroll-fixed6">
        <div className="row align-center space-between sales-entry-head">
          <p className="caps login-heading page-heading page-heading-d">&nbsp;</p>
          <div className="row space-between align-center gap-15">
            <button onClick={handlePrint} className="list-btn gold-back-btn sales-btn-desktop">PRINT</button>
            <button onClick={saveSale} className="list-btn gold-back-btn sales-btn-desktop">SAVE</button>
            <button
              onClick={() => {
                setIsLinkModalOpen(true);
                fetchActiveBookings();
                fetchRoomsForDisplay();
              }}
              className="list-btn gold-back-btn sales-btn-desktop"
            >
              LINK
            </button>
          </div>
        </div>
        <div className="sales-entry">
          <div className="sales-entry-input-fields">
            <div className="input-group">
              <div className="row align-start sales-search-scan-field gap-10" style={{position: 'relative'}}>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="input-field1 input-field1-desktop"
                  placeholder="Search by product name, category or barcode"
                />
                {searchQuery && (
                  <button
                    className="clear-search-btn"
                    onClick={() => setSearchQuery("")}                  
                  >
                    <i className="fas fa-times font-red"></i>
                  </button>
                )}
                <button
                  className="list-btn scan-btn gold-back-btn"
                  onClick={() => setIsScanModalOpen(true)}
                >
                  <i className="fas fa-barcode"></i>
                </button>
              </div>
              {filteredProducts.length > 0 && (
                <ul className="product-dropdown">
                  {filteredProducts.map((product) => {
                    const qty = product.quantity ?? 0;
                    const hasMinStock = product.minStockLevel != null && product.minStockLevel > 0;

                    const isOutOfStock = qty <= 0;
                    const isLowStock = !isOutOfStock && hasMinStock && qty <= product.minStockLevel;

                    // You can make out-of-stock items look more "disabled"
                    const itemStyle = isOutOfStock
                      ? { color: '#999', fontStyle: 'italic', opacity: 0.7 }
                      : isLowStock
                        ? { color: '#fc796f', fontWeight: '500' }
                        : {};

                    return (
                      <li
                        key={product.id}
                        onClick={() => {                          
                          setSelectedProduct(product.id);
                        }}
                        className="product-dropdown-item"
                        style={itemStyle}
                      >
                        <div className="row space-between align-center" style={{ width: '100%' }}>
                          <div>
                            {product.name} (₦ {product.price?.toLocaleString() || product.price})
                            <br />
                            <small>
                              Qty: {qty} 
                              {product.category && ` | ${product.category}`}
                            </small>
                          </div>

                          {/* Badges - OUT OF STOCK takes priority */}
                          {isOutOfStock ? (
                            <span
                              style={{
                                background: '#555',
                                color: '#ffdddd',
                                padding: '3px 10px',
                                borderRadius: '12px',
                                fontSize: '0.78rem',
                                fontWeight: 'bold',
                                textTransform: 'uppercase',
                              }}
                            >
                              OUT OF STOCK
                            </span>
                          ) : isLowStock ? (
                            <span
                              style={{
                                background: '#fc796f',
                                color: 'white',
                                padding: '2px 8px',
                                borderRadius: '10px',
                                fontSize: '0.78rem',
                                fontWeight: 'bold',
                              }}
                            >
                              LOW STOCK
                            </span>
                          ) : null}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            <div className="customer-details">
              <div className="row gap-10">
                <input
                  type="text"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  className="input-field1"
                  placeholder="Customer Name"
                />
                <input
                  type="text"
                  value={customerPhone}
                  onChange={(e) => setCustomerPhone(e.target.value)}
                  className="input-field1"
                  placeholder="Phone"
                />
              </div>
            </div>
          </div>
          <div className="cart scroll-fixed5">        
            {cart.length > 0 ? (
              <table>
                <thead>
                  <tr style={{ backgroundColor: '#181818' }}>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #464646', fontWeight: 'bold' }}>Product/Quantity</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #464646', fontWeight: 'bold' }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {cart.map((item) => (
                    <tr key={item.id} style={{ borderBottom: '1px solid #464646' }}>
                      <td style={{ padding: '12px', textAlign: 'left' }}>
                        <span>{item.name}</span>
                        <div className="row align-center gap-10">
                          <button
                            onClick={() => decrementQuantity(item.id)}
                            className="quantity-button"
                          >
                            -
                          </button>
                          <input
                            type="number"
                            value={item.quantity}
                            onChange={(e) => updateQuantity(item.id, e.target.value)}
                            className="quantity-input"
                            min="1"
                          />
                          <button
                            onClick={() => incrementQuantity(item.id)}
                            className="quantity-button"
                          >
                            +
                          </button>
                        </div>
                      </td>
                      <td className="row space-between" style={{ padding: '12px', textAlign: 'right' }}>
                        <span>₦ {item.subtotal.toLocaleString()}</span>
                        <button onClick={() => removeFromCart(item.id)} className="remove-button">
                          <i className="fas fa-trash-alt font-red"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td className="space-between" colSpan="2" style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>
                      <span>Grand Total</span>
                      <span>₦ {calculateTotal().toLocaleString()}</span>
                    </td>
                  </tr>
                </tfoot>
              </table>
            ) : (
              <p style={{ textAlign: 'center', marginTop: '20px' }}>No items in cart</p>
            )}
          </div>
        </div>
        {printContent && (
          <div className="print-section" style={{ display: 'none' }}>
            <pre>{printContent}</pre>
          </div>
        )}
        <Modal
          isOpen={isScanModalOpen}
          onRequestClose={() => setIsScanModalOpen(false)}
          contentLabel="Scan Barcode"
          className="custom-modal"
          overlayClassName="custom-modal-overlay"
        >
          <h2>Scan Barcode</h2>
          <Html5QrcodePlugin
            fps={10}
            qrbox={250}
            disableFlip={false}
            qrCodeSuccessCallback={onNewScanResult}
            autoStart={true}
          />
          <button className="login-button" onClick={() => setIsScanModalOpen(false)}>Close Scanner</button>
        </Modal>

        <Modal
          isOpen={isLinkModalOpen}
          onRequestClose={() => {
            setIsLinkModalOpen(false);
            setBookingSearch("");
            setSelectedBooking(null);
          }}
          contentLabel="Link Sale to Booking"
          className="custom-modal"
          overlayClassName="custom-modal-overlay"
        >
          <h2 style={{ marginBottom: '16px' }}>Link Sale to Booking</h2>

          <div style={{ marginBottom: '16px', position: 'relative' }}>
            <input
              type="text"
              value={bookingSearch}
              onChange={(e) => setBookingSearch(e.target.value)}
              placeholder="Search guest name, phone or room number..."
              className="input-field1"
              style={{ width: '100%' }}
            />
          </div>

          <div 
          className="modal-scroll extra-amenities"
          style={{
            maxHeight: '320px'
          }}>
            {bookings.length === 0 ? (
              <p style={{ textAlign: 'center', color: '#888', padding: '20px' }}>
                No active bookings found
              </p>
            ) : (
              bookings
                .filter(b => {
                  if (!bookingSearch.trim()) return true;
                  const term = bookingSearch.toLowerCase();
                  const guestNameMatch = (b.guestName || '').toLowerCase().includes(term);
                  const phoneMatch   = (b.guestPhone || '').toLowerCase().includes(term);
                  const roomMatch    = (b.roomNumber || '').toLowerCase().includes(term); // if you store roomNumber
                  return guestNameMatch || phoneMatch || roomMatch;
                })
                .map(booking => (
                  <div
                    key={booking.id}
                    onClick={() => setSelectedBooking(booking)}
                    style={{
                      padding: '10px 12px',
                      marginBottom: '6px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      background: '#222',
                      border: selectedBooking?.id === booking.id ? '1px solid #e9e9e9' : '1px solid #444'
                    }}
                  >
                    <div style={{ fontWeight: 'bold' }}>
                      {booking.guestName || "Unknown Guest"} ({booking.guestPhone || '-'})
                    </div>
                    <div>
                      Room: {roomMap[booking.roomId] || booking.roomId?.slice(0,8) || '-'}
                      <br />
                      <span className="space-between">
                        <span>Check-in: {booking.checkIn?.toDate?.()?.toLocaleDateString() || '-'}</span> <span className="font-gold bold">{booking.status}</span>
                      </span>
                    </div>
                  </div>
                ))
            )}
          </div>

          <div className="row gap-15">
            <button
              onClick={() => {
                setIsLinkModalOpen(false);
                setBookingSearch("");
                setSelectedBooking(null);
              }}
              className="login-button"
              style={{ marginRight: '12px', background: '#444' }}
            >
              Cancel
            </button>

            <button
              disabled={!selectedBooking || linkLoading || cart.length === 0}
              onClick={handleLinkToBooking}
              className="login-button"
            >
              {linkLoading ? "Linking..." : "Link Sale"}
            </button>
          </div>
        </Modal>

        {showModal && (
          <SuccessModal
            icon={error ? "xmark" : "check"}
            iconColor={error ? "red" : "green"}
            message={error || successMessage}
            onClose={() => {
              setError("");
              setSuccessMessage("");
              setShowModal(false);
            }}
          />
        )}
      </div>
    </>
  );
};

export default SalesEntry;